import React, { useState, useEffect, useRef } from 'react'
import { useLocation, useHistory, useParams } from 'react-router-dom'
import moment from 'moment'
import { message, Modal } from 'antd'
import { MainHeading, Divider, DatePicker, SpinnerDotted, Spinner, Breadcrumb, DSIcon } from '../../components/common/atoms'
import { Table, Pagination } from '../../components/common/molecules'
import { handleAppError } from '../../components/handlers'
import { RaiseDisputeModal, InvoiceReviewDisputeModal, RequestInvoiceDateModal } from '../../components/modals'
import { PngExportFrame } from '../../components/opdClaims/primitives'
import { successOrFailureToaster } from '../../components/common/atoms/messages'
import { PARTICIPATION_BREAK_UP_HEADERS, PARTICIPATION_LIST_HEADERS } from '../../constants/tableHeaders'
import { invoiceBreakUpSchema, misParticipantsListSchema } from '../../schemas/table'
import { popup_close as closebtn, white_tick_icon, x_red, exclametry_red_dark } from '../../assets/images'
import InvoicesApi from '../../services/invoices.api'
import { SALES_ROLE } from '../../constants/overview'
import { dsButtonPrimary, dsButtonSecondary, dsButtonGhostBrand, dsButtonSpinnerInline, joinButtonClasses } from '../../utils/buttonStyles'
import { BADGE_TONE_CLASSES, isUnderReview, listStatusBadge, isReleased, approveSuccessMessage, breakUpShape, reviewBanner, reviewActions, disputePayload, toggleSelection, requestDateRange, serverMessage, participantListKind } from './invoiceReview'

// shape comes from breakUpShape, the same decision the break-up rows use
const getBreakUpHeaders = (shape, service, gst_percentage) => {
  service = (service || '').trim().toLowerCase()
  if (shape === 'ppu') return PARTICIPATION_BREAK_UP_HEADERS['ppu_headers'](service, gst_percentage)
  if (shape === 'subscription') return PARTICIPATION_BREAK_UP_HEADERS['subscription_headers'](gst_percentage)
  const callback = PARTICIPATION_BREAK_UP_HEADERS[shape]
  return callback ? callback(gst_percentage) : []
}

// Service labels come from benefit names, so the response's shape (kind) decides; the label is the fallback
const getParticipationListHeaders = (service, kind) => {
  if (kind === 'ppu') return PARTICIPATION_LIST_HEADERS['ppu_headers']
  if (kind === 'subscription') return PARTICIPATION_LIST_HEADERS['subscription_headers']
  service = (service || '').trim().toLowerCase()

  let headers = []
  if (['health checks', 'vaccination', 'vision', 'dental', 'onsite', 'engagement activity', 'ohc'].includes(service)) headers = PARTICIPATION_LIST_HEADERS['ppu_headers']
  else if (['doctor consultation', 'opd', 'ewap', 'smart cover', 'gym', 'employee addition', 'health coach'].includes(service)) headers = PARTICIPATION_LIST_HEADERS['subscription_headers']

  return headers
}

const MISInvoices = () => {
  const location = useLocation()
  const history = useHistory()
  const { invoiceNo } = useParams()
  // location.state is only set when arriving from the invoices list; a direct load or refresh has none
  const pathState = (location && location.state) || {}
  const hasState = !!(pathState.invoice_id && pathState.invoice_item_id)
  const [data, setData] = useState({ table_loading: true, summary_loading: true })
  const [paginationData, setPaginationData] = useState({ total_pages: 0, cur_page: 0, total_results: 0 })
  const [flags, setFlags] = useState({ disputeModal: false, spinner: false })
  // HR review: invoice_assignment_ids ticked across pages, and which review modal is open
  const [selectedIds, setSelectedIds] = useState([])
  const [reviewModal, setReviewModal] = useState({ open: null, submitting: false, error: '' })
  const [approving, setApproving] = useState(false)
  const pageRequest = useRef(0)
  const dates = useRef({ start_date: pathState.start_date || moment().startOf('month').format('DD-MM-YYYY'), end_date: pathState.end_date || moment().endOf('month').format('DD-MM-YYYY') })

  const fetchData = () => {
    // a page request still in flight must not overwrite this reload, nor a later page click this one
    const requestId = ++pageRequest.current
    setData(prevState => { return { ...prevState, table_loading: true, summary_loading: true } })
    // the backend pages from 1 (kaminari); cur_page below stays 0-based for react-paginate
    const data = { company_invoice_id: pathState.invoice_id, company_invoice_item_id: pathState.invoice_item_id, page: 1 }
    const promise1 = InvoicesApi.fetch_mis_summary({ data })
    const promise2 = InvoicesApi.fetch_participants_list({ data })

    Promise.all([promise1, promise2])
      .then((values) => {
        const resp_arr = values.map((resp) => resp.data || {})
        // overtaken by a page click: keep the fresh summary (review state), leave that page's rows alone
        if (requestId !== pageRequest.current) {
          setData(prevState => { return { ...prevState, ...resp_arr[0], summary_loading: false, loaded: true } })
          return
        }
        const total_results = (resp_arr[1].meta && resp_arr[1].meta.total) || 0
        setPaginationData({ total_pages: Math.ceil(total_results / 10), cur_page: 0, total_results })
        // loaded: actions only render for an invoice that was loaded at least once
        setData({ ...resp_arr[0], ...resp_arr[1], table_loading: false, summary_loading: false, loaded: true })
      })
      .catch((err) => {
        handleAppError(err)
        setData(prevState => { return { ...prevState, table_loading: requestId === pageRequest.current ? false : prevState.table_loading, summary_loading: false } })
      })
  }

  const navigateToPage = (page) => {
    // only the latest page request may write; a slower earlier one is dropped
    const requestId = ++pageRequest.current
    setData(prevState => { return { ...prevState, table_loading: true } })
    InvoicesApi.fetch_participants_list({ data: { company_invoice_id: pathState.invoice_id, company_invoice_item_id: pathState.invoice_item_id, page: page + 1 } },
      (res) => {
        if (requestId !== pageRequest.current) return
        const data = res.data || {}
        setPaginationData(prevState => { return { ...prevState, cur_page: page } })
        // drop the previous page's rows so they can never pair with this page's invoice_assignment_ids
        setData(prevState => {
          const { appointments, customers, ...rest } = prevState
          return { ...rest, ...data, table_loading: false }
        })
      },
      (err) => {
        if (requestId !== pageRequest.current) return
        handleAppError(err)
        setData(prevState => { return { ...prevState, table_loading: false } })
      }
    )
  }

  const disputeHandler = (type, comment) => {
    if (['close', 'open'].includes(type)) {
      setFlags(prevState => { return { ...prevState, disputeModal: !prevState.disputeModal } })
    } else if (type === 'submit') {
      setFlags({ spinner: true, disputeModal: false })
      const data = { company_invoice_id: pathState.invoice_id, company_invoice_item_id: pathState.invoice_item_id, comment }
      InvoicesApi.dispute_invoice({ data },
        (_) => {
          setFlags({ spinner: false, disputeModal: false })
          fetchData()
          successOrFailureToaster({ message: 'Invoice has been rejected', type: 'custom', customColor: '#C80010', customIcon: exclametry_red_dark })
        },
        (err) => {
          const message = serverMessage(err, 'Error occurred while raising dispute')
          setFlags({ spinner: false, disputeModal: false })
          successOrFailureToaster({ message, type: 'fail' })
        }
      )
    }
  }

  const confirmHandler = () => {
    setFlags({ spinner: true, disputeModal: false })
    const data = { company_invoice_id: pathState.invoice_id, company_invoice_item_id: pathState.invoice_item_id }
    InvoicesApi.confirm_invoice({ data },
      (_) => {
        setFlags({ spinner: false, disputeModal: false })
        fetchData()
        successOrFailureToaster({ message: 'Invoice has been confirmed' })
      },
      (err) => {
        const message = serverMessage(err, 'Error occurred while confirming invoice')
        setFlags({ spinner: false, disputeModal: false })
        successOrFailureToaster({ message, type: 'fail' })
      }
    )
  }

  const reviewRequestData = { company_invoice_id: pathState.invoice_id, company_invoice_item_id: pathState.invoice_item_id }

  const isConflict = (err) => !!(err && err.response && err.response.status === 409)

  // 409 means the review moved on elsewhere (e.g. already approved); refresh so the page shows it
  const refreshOnConflict = (err) => {
    if (isConflict(err)) fetchData()
  }

  // Inline error in the open modal, except on a 409: the action no longer applies, so close it
  const reviewModalError = (err, fallback) => {
    const message = serverMessage(err, fallback)
    if (isConflict(err)) {
      setReviewModal({ open: null, submitting: false, error: '' })
      successOrFailureToaster({ message, type: 'fail' })
      fetchData()
    } else {
      setReviewModal(prevState => { return { ...prevState, submitting: false, error: message } })
    }
  }

  const approveHandler = () => {
    if (approving) return
    const successMessage = approveSuccessMessage(data.review)
    Modal.confirm({
      title: `Approve invoice ${invoiceNo}?`,
      content: 'It will be issued and mailed to you.',
      okText: 'Approve',
      onOk: () => new Promise((resolve) => {
        // stays set until the request settles, even if the dialog is dismissed meanwhile
        setApproving(true)
        InvoicesApi.approve_invoice({ data: reviewRequestData },
          (_) => {
            resolve()
            setApproving(false)
            // show the approved state straight away; the refetch then replaces it with the server's
            setData(prevState => { return { ...prevState, review: { ...(prevState.review || {}), status: 'approved' } } })
            setSelectedIds([])
            fetchData()
            successOrFailureToaster({ message: successMessage })
          },
          (err) => {
            resolve()
            setApproving(false)
            refreshOnConflict(err)
            successOrFailureToaster({ message: serverMessage(err, 'Error occurred while approving invoice'), type: 'fail' })
          }
        )
      })
    })
  }

  const reviewDisputeHandler = ({ reasonCode, comment, scope }) => {
    setReviewModal(prevState => { return { ...prevState, submitting: true, error: '' } })
    const data = disputePayload({ invoiceId: pathState.invoice_id, invoiceItemId: pathState.invoice_item_id, reasonCode, comment, scope, selectedIds })
    InvoicesApi.dispute_invoice({ data },
      (_) => {
        setReviewModal({ open: null, submitting: false, error: '' })
        setData(prevState => { return { ...prevState, review: { ...(prevState.review || {}), status: 'disputed' } } })
        setSelectedIds([])
        fetchData()
        successOrFailureToaster({ message: 'Sent to ekincare finance' })
      },
      (err) => reviewModalError(err, 'Error occurred while raising dispute')
    )
  }

  const requestDateHandler = (month) => {
    setReviewModal(prevState => { return { ...prevState, submitting: true, error: '' } })
    InvoicesApi.request_invoice_date({ data: { ...reviewRequestData, month } },
      (res) => {
        setReviewModal({ open: null, submitting: false, error: '' })
        // show the open request straight away (hides the button) even if the refetch fails
        const requested_date = res.data && res.data.requested_date
        setData(prevState => { return { ...prevState, review: { ...(prevState.review || {}), date_request: { status: 'open', requested_date, resolution: null, note: null } } } })
        fetchData()
        // the backend's success message is just "Success"
        successOrFailureToaster({ message: 'Date change requested — ekincare finance will get back to you' })
      },
      (err) => reviewModalError(err, 'Error occurred while requesting the date change')
    )
  }

  const openReviewModal = (open) => setReviewModal({ open, submitting: false, error: '' })
  const closeReviewModal = () => setReviewModal(prevState => prevState.submitting ? prevState : { open: null, submitting: false, error: '' })
  const toggleRows = (ids, checked) => setSelectedIds(prevState => toggleSelection(prevState, ids, checked))

  const downloadHadnler = () => {
    setFlags({ spinner: true, disputeModal: false })
    const data = { company_invoice_id: pathState.invoice_id, company_invoice_item_id: pathState.invoice_item_id }
    InvoicesApi.download_participation_list({ data, responseType: 'blob' },
      (res) => {
        const url = window.URL.createObjectURL(new Blob([res.data], { type: res.headers['content-type'] }))
        const link = document.createElement('a')
        let fileName = 'participation list.csv'
        if (res.headers['content-type'] === 'application/pdf') {
          fileName = 'participation list.pdf'
        } else if (['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'].includes(res.headers['content-type'])) {
          fileName = 'participation list.xlsx'
        }
        link.href = url
        link.setAttribute('download', fileName)
        link.click()
        link.remove()

        setFlags({ spinner: false, disputeModal: false })
      },
      (err) => {
        const message = (err.response && err.response.data && err.response.data.msg) || 'Error occurred while downloading participation list'
        setFlags({ spinner: false, disputeModal: false })
        successOrFailureToaster({ message, type: 'fail' })
      }
    )
  }

  useEffect(() => {
    if (!hasState) {
      message.info('Please open the participation list from the invoices page')
      history.replace('/invoices')
      return
    }
    fetchData()
  }, [])

  if (!hasState) return null

  const service = (pathState.service || '').trim().toLowerCase()
  const isInvoiceRejected = (data.invoice_status || '').trim().toLowerCase() === 'rejected'
  const isInvoiceConfirmed = ['confirmed', 'auto confirmed'].includes((data.invoice_status || '').trim().toLowerCase())
  const isStaff = (sessionStorage.getItem('role') || '').toLowerCase() === 'staff'
  const tableData = (data.appointments || data.customers || [])
  const listKind = participantListKind(data)
  const hasTable = getParticipationListHeaders(service, listKind).length > 0 && tableData.length > 0

  // HR review (review is null for invoices that never went through it)
  const review = data.review || null
  const banner = reviewBanner(review)
  const actions = reviewActions({ review, role: sessionStorage.getItem('role'), salesRole: SALES_ROLE })
  const decisionBadge = !isUnderReview(review) ? listStatusBadge({ invoice_status: data.invoice_status, review }) : null
  // after release, master's post-release confirm/dispute window applies again
  const released = !!data.loaded && isReleased({ review, invoiceStatus: data.invoice_status })
  const breakUp = breakUpShape(service, listKind, data.invoice_item_details)
  const pageIds = ((data.meta && data.meta.invoice_assignment_ids) || []).filter(id => id !== undefined && id !== null)
  const selection = actions.visible && pageIds.length > 0 ? { ids: (data.meta || {}).invoice_assignment_ids, selected: selectedIds, onToggle: toggleRows } : undefined
  const pageAllSelected = pageIds.length > 0 && pageIds.every(id => selectedIds.includes(id))
  const pageCheckbox = <input type='checkbox' checked={pageAllSelected} onChange={(event) => toggleRows(pageIds, event.target.checked)} className='rounded-sm text-blue-600 h-4 w-4 border-gray-300 focus:ring-0 focus:ring-offset-0 cursor-pointer' />
  const listHeaders = selection ? [pageCheckbox, ...getParticipationListHeaders(service, listKind)] : getParticipationListHeaders(service, listKind)

  return (
    <Spinner visible={flags.spinner} >
      <div className='min-h-screen bg-gray-100 p-8'>
        <Breadcrumb list={[{ text: 'Invoices', link: '/invoices' }, { text: 'Participation list', link: '#' }]} classes='mb-1' />
        <div className='flex justify-between items-center'>
          <MainHeading mainHeading={pathState.service} />
          <img src={closebtn} alt='close-btn' className='cursor-pointer h-8 w-8' onClick={() => history.goBack()} />
        </div>

        <Divider classes='mt-5 mb-6' />

        {/* summary */}
        <div className={`bg-white border border-gray-300 rounded-ds-lg p-ds-6 ${!data.summary_loading ? '' : 'flex items-center justify-center h-44'}`}>
          {!data.summary_loading ?
            <React.Fragment>
              <PngExportFrame fileName='invoice-breakup'>
              <div className='flex'>
                <table className='w-4/5 border-separate'>
                  <thead>
                    <tr>
                      {
                        getBreakUpHeaders(breakUp, service, data.gst_percentage || 0).map(item => <th className='border-b border-gray-300 bg-gray-50 px-4 py-3 text-left text-2xs font-bold uppercase tracking-widest text-gray-600'>{item}</th>)
                      }
                    </tr>
                  </thead>
                  <tbody className='text-gray-900'>
                    {
                      (data['invoice_item_details'] || []).length > 0 ? (data['invoice_item_details'] || []).map(item_data => {
                        return (
                          invoiceBreakUpSchema(breakUp, item_data)
                        )
                      }) : (
                        <tr>
                          <td colSpan={getBreakUpHeaders(breakUp, service, data.gst_percentage || 0).length} className="px-4 py-8 text-center text-sm text-gray-500 font-fig-normal">
                            No results found
                          </td>
                        </tr>
                      )
                    }
                  </tbody>
                </table>
                <Divider type='vertical' classes='mt-2 border-gray-300' />
                <div className='w-1/5 flex flex-col items-center justify-center gap-ds-1'>
                  <span className='text-ts-xs font-semibold uppercase tracking-[0.1em] text-gray-500'>Amount to be paid</span>
                  <span className='font-jetbrains-mono text-ts-2xl font-semibold tabular-nums text-gray-900'>₹{data['total_amount'] || 0}</span>
                  {(isInvoiceConfirmed || isInvoiceRejected) && <span className={`mt-ds-1 rounded-ds-pill px-ds-3 py-1 text-ts-xs font-semibold ${isInvoiceConfirmed ? 'text-ds-green-dk bg-ds-green-lt' : 'text-ds-rose-dk bg-ds-rose-lt'}`}>{data.invoice_status}</span>}
                  {!(isInvoiceConfirmed || isInvoiceRejected) && decisionBadge && <span className={`mt-ds-1 rounded-ds-pill px-ds-3 py-1 text-ts-xs font-semibold text-center ${BADGE_TONE_CLASSES[decisionBadge.tone]}`}>{decisionBadge.text}</span>}
                </div>
              </div>
              </PngExportFrame>
              {banner &&
                <React.Fragment>
                  <Divider classes='border-gray-300 my-6' />
                  <div className='rounded-ds-md border border-ds-amber-200 bg-ds-amber-lt p-4 text-gray-900'>
                    <p className='mb-1 font-semibold'>{banner.due}</p>
                    {banner.invoiceDate && <p className='mb-0'>{banner.invoiceDate}</p>}
                    {banner.dateRequest && <p className='mb-0 mt-1 text-gray-700'>{banner.dateRequest}</p>}
                  </div>
                  {actions.visible &&
                    <div className='mt-6 flex justify-center items-center gap-3'>
                      <button
                        onClick={approveHandler}
                        disabled={actions.disabled || approving}
                        className={joinButtonClasses(dsButtonPrimary, 'h-11 px-6', actions.disabled && 'cursor-not-allowed', approving && dsButtonSpinnerInline)}
                      >
                        <img src={white_tick_icon} alt='white-tick' className='inline mr-2' />
                        Approve
                      </button>
                      <button
                        onClick={() => openReviewModal('dispute')}
                        disabled={actions.disabled || approving}
                        className={joinButtonClasses(dsButtonSecondary, 'h-11 px-6', (actions.disabled || approving) && 'cursor-not-allowed')}
                      >
                        <img src={x_red} alt='red-close' className='inline mr-2' />
                        Dispute
                      </button>
                      {selectedIds.length > 0 && <span className='text-gray-600 font-semibold'>{selectedIds.length} {selectedIds.length === 1 ? 'row' : 'rows'} selected</span>}
                      {actions.canRequestDate &&
                        <button
                          onClick={() => openReviewModal('date')}
                          disabled={actions.disabled || approving}
                          className={joinButtonClasses(dsButtonGhostBrand, 'h-11 px-6', (actions.disabled || approving) && 'cursor-not-allowed')}
                        >
                          Request a different date
                        </button>
                      }
                    </div>
                  }
                </React.Fragment>
              }
              {/* master's confirm/dispute, once the invoice is released (or never went through HR review) */}
              {released && !isInvoiceConfirmed && (isStaff || isInvoiceRejected) &&
                <React.Fragment>
                  <Divider classes='border-gray-300 my-6' />
                  <div className={`flex ${isInvoiceRejected ? 'flex-col' : 'justify-center items-center'}`}>
                    <p className='mb-0 text-gray-900 font-semibold'>{isInvoiceRejected ? 'Reason for rejection' : 'Please accept the invoice by 06th of every month or else invoice will be auto confirmed'}</p>
                    {isInvoiceRejected ?
                      <React.Fragment>
                        <p className='bg-white mb-0 mt-3 rounded-ds-md border border-gray-300 p-4 text-gray-900 font-semibold'>{data.comment}</p>
                      </React.Fragment>
                      :
                      <React.Fragment>
                        <button
                          onClick={confirmHandler}
                          disabled={SALES_ROLE === "sales"}
                          className={joinButtonClasses(dsButtonPrimary, 'ml-16 h-11 px-6', SALES_ROLE === 'sales' && 'cursor-not-allowed')}
                        >
                          <img src={white_tick_icon} alt='white-tick' className='inline mr-2' />
                          Confirm
                        </button>
                        <button
                          onClick={() => disputeHandler('open')}
                          disabled={SALES_ROLE === "sales"}
                          className={joinButtonClasses(dsButtonSecondary, 'ml-3 h-11 px-6', SALES_ROLE === 'sales' && 'cursor-not-allowed')}
                        >
                          <img src={x_red} alt='red-close' className='inline mr-2' />
                          Raise dispute
                        </button>
                      </React.Fragment>
                    }
                  </div>
                </React.Fragment>
              }
            </React.Fragment>
            :
            <SpinnerDotted />
          }
        </div>


        {/* filter */}
        <div className='bg-white border border-gray-300 rounded-ds-lg px-4 py-3 flex justify-between items-center mt-8'>
          <div className='flex items-center'>
            <span className='text-ts-xs font-semibold uppercase tracking-[0.1em] text-gray-500 mr-3'>From date</span><DatePicker classes='mr-6' value={dates.current.start_date} disabled={true} />
            <span className='text-ts-xs font-semibold uppercase tracking-[0.1em] text-gray-500 mr-3'>To date</span><DatePicker value={dates.current.end_date} disabled={true} />
          </div>
          <button
            onClick={downloadHadnler}
            disabled={SALES_ROLE === "sales"}
            className={joinButtonClasses(dsButtonGhostBrand, 'h-11 px-6', SALES_ROLE === 'sales' && 'cursor-not-allowed')}
          >
            <DSIcon name='download' size={18} className='mr-2' />
            Download participants list
          </button>
        </div>

        {/* participation list */}
        {listHeaders.length > 0 ? (
          <React.Fragment>
            <Table
              loading={data.table_loading}
              headers={listHeaders}
              tableschema={misParticipantsListSchema(service, listKind)}
              data={tableData}
              selection={selection}
              classes='mt-6 rounded-ds-lg'
              tableClasses='alternate_gray200_color'
            />
            {hasTable &&
              <Pagination
                totalpages={paginationData.total_pages}
                totalresults={paginationData.total_results}
                curpage={paginationData.cur_page}
                handler={navigateToPage}
              />
            }
          </React.Fragment>
        ) : (
          !data.table_loading &&
          <div className='mt-6 rounded-ds-lg border border-gray-300 bg-white px-4 py-12 text-center text-ts-sm text-gray-500'>
            No participants to display
          </div>
        )}
        <RaiseDisputeModal visible={flags.disputeModal} service={pathState.service || ''} bill_amount={data['total_amount'] || 0} close={() => disputeHandler('close')} submitHandler={(v) => disputeHandler('submit', v)} />
        <InvoiceReviewDisputeModal
          visible={reviewModal.open === 'dispute'}
          invoiceNo={invoiceNo}
          selectedCount={selectedIds.length}
          submitting={reviewModal.submitting}
          serverError={reviewModal.error}
          close={closeReviewModal}
          submitHandler={reviewDisputeHandler}
        />
        <RequestInvoiceDateModal
          visible={reviewModal.open === 'date'}
          invoiceNo={invoiceNo}
          range={requestDateRange({ billedMonth: review && review.billed_month, startDate: pathState.start_date })}
          submitting={reviewModal.submitting}
          serverError={reviewModal.error}
          close={closeReviewModal}
          submitHandler={requestDateHandler}
        />
      </div>
    </Spinner>
  )
}

export default MISInvoices
